import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useTopSellingQuery } from "../../Redux/Api/top";
import { usePriceMutation } from "../../Redux/Api/priceAPI";
import { addToCart } from "../../Redux/reducer/cartReducer";
import { ORDERING_ENABLED } from "../../config/features";
import { exGst, DEFAULT_GST_PERCENT } from "../../utils/priceText";
import { CiButton, RateLine, SectionTitle } from "../../common/ci/Ui";
import { useCiT } from "../../common/ci/i18n";
import defaultImage from "../../assets/defaultImage.svg";

interface TopRow {
  id: string;
  name?: string;
  productName?: string;
  image?: string;
  companyName?: string;
  view?: number;
}

interface PriceRow {
  productId: string;
  finalPrice?: number;
  unit?: string;
  highLowPrice?: { lowFinalPrice?: number; highFinalPrice?: number } | null;
  gstPercent?: number;
  taxableValue?: number;
}

/**
 * "Popular this week" — wahi /global/top/data/product jo purana TopSelling use
 * karta tha, par design ke row-card me: naam · brand · rate ex-GST + GST · Add.
 * Rate purane getPrice se (GST-inclusive) → exGst() se ex-GST; API jab
 * taxableValue/gstPercent bhejegi (ticket 1.3) to wahi use hoga.
 */
const PopularProducts = () => {
  const t = useCiT();
  const dispatch = useDispatch();
  const { data: selling } = useTopSellingQuery();
  const [priceMutation] = usePriceMutation();
  const [prices, setPrices] = useState<Record<string, PriceRow>>({});

  const rows: TopRow[] = Array.isArray(selling) ? selling.slice(0, 6) : [];

  useEffect(() => {
    const ids = rows.map((r) => r.id).filter(Boolean);
    if (!ids.length) return;
    let alive = true;
    (async () => {
      try {
        const res: any = await priceMutation({ productId: ids });
        const map: Record<string, PriceRow> = {};
        (res?.data?.results || []).forEach((p: PriceRow) => {
          map[p.productId] = p;
        });
        if (alive) setPrices(map);
      } catch (e) {
        // rate na mile to card bina rate ke dikhega
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selling]);

  if (!rows.length) return null;

  const rateOf = (p?: PriceRow): { rate: number | null; gst: number; unit?: string } => {
    if (!p) return { rate: null, gst: DEFAULT_GST_PERCENT };
    const gst = Number.isFinite(Number(p.gstPercent)) ? Number(p.gstPercent) : DEFAULT_GST_PERCENT;
    if (Number.isFinite(Number(p.taxableValue)) && Number(p.taxableValue) > 0) {
      return { rate: Number(p.taxableValue), gst, unit: p.unit };
    }
    const incl = p.highLowPrice?.lowFinalPrice ?? p.finalPrice;
    const ex = exGst(incl, gst);
    return { rate: Number.isFinite(ex) && ex > 0 ? ex : null, gst, unit: p.unit };
  };

  const add = (r: TopRow, unit?: string) => {
    if (!ORDERING_ENABLED) return;
    dispatch(
      addToCart({
        companyProductId: r.id,
        quantity: 1,
        itemName: r.productName || r.name,
        companyName: r.companyName,
        unit,
        image: r.image,
      })
    );
    toast.success(t("Cart me add ho gaya"));
  };

  return (
    <div>
      <SectionTitle right={<span className="text-ci-muted">{t("Rates incl. GST")}</span>}>{t("Popular this week")}</SectionTitle>
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((r) => {
          const p = rateOf(prices[r.id]);
          const name = r.productName || r.name || "Item";
          return (
            <div
              key={r.id}
              className="flex items-center gap-3 rounded-lg border border-ci-line bg-white p-2.5 sm:p-3"
            >
              <Link
                to={`/product/${r.id}/${encodeURIComponent(name)}`}
                className="h-[64px] w-[72px] shrink-0 overflow-hidden rounded-md bg-[#eceae5]"
              >
                <img
                  src={r.image || defaultImage}
                  alt={name}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </Link>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <Link
                  to={`/product/${r.id}/${encodeURIComponent(name)}`}
                  className="truncate text-left text-[14px] font-semibold hover:text-ci-navy"
                >
                  {name}
                </Link>
                {r.companyName ? (
                  <span className="truncate text-[12px] text-ci-muted">{r.companyName}</span>
                ) : null}
                <RateLine rate={p.rate} unit={p.unit} gstPercent={p.gst} size="sm" />
              </div>
              {ORDERING_ENABLED ? (
                <CiButton size="sm" variant="navy" onClick={() => add(r, p.unit)}>
                  {t("Add")}
                </CiButton>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PopularProducts;
